// overview/CompactGridView — grid-vy med en cell per grupp.
// Extraherad ur OverviewDialog (#6).

import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import { CrossGroupConnectors } from './shared';

export function CompactGridView({
  blocks,
  groups,
  crossGroupConnectors,
  resolveName,
  resolveColor,
}: {
  blocks: WorkflowBlock[];
  groups: BlockGroup[];
  crossGroupConnectors: Connector[];
  resolveName: (id: string) => string;
  resolveColor: (id: string) => string;
}) {
  const topLevelGroups = groups.filter(g => !g.parentGroupId);
  const childGroupsOf = (parentId: string) => groups.filter(g => g.parentGroupId === parentId);
  const allGroupedBlockIds = new Set(groups.flatMap(g => g.blockIds));
  const ungroupedBlocks = blocks.filter(b => !allGroupedBlockIds.has(b.id));

  function renderGroupCard(group: BlockGroup): React.ReactNode {
    const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
    const children = childGroupsOf(group.id);

    return (
      <div
        key={group.id}
        className="rounded-xl border-2 p-4 flex flex-col"
        style={{
          borderColor: group.color,
          backgroundColor: `${group.color}08`,
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: group.color }} />
          <span className="text-sm font-bold" style={{ color: group.color }}>
            {group.name || 'Group'}
          </span>
        </div>
        <span className="text-xs text-muted-foreground mb-2">
          {groupBlocks.length} blocks
        </span>
        <div className="flex flex-wrap gap-1">
          {groupBlocks.slice(0, 6).map(b => (
            <span
              key={b.id}
              className="text-[10px] px-1.5 py-0.5 rounded border"
              style={{
                borderColor: `${b.color}40`,
                color: b.color,
                backgroundColor: `${b.color}10`,
              }}
            >
              {b.config.name}
            </span>
          ))}
          {groupBlocks.length > 6 && (
            <span className="text-[10px] px-1.5 py-0.5 text-muted-foreground">
              +{groupBlocks.length - 6} more
            </span>
          )}
        </div>
        {children.length > 0 && (
          <div className="mt-2 pt-2 border-t border-dashed" style={{ borderColor: `${group.color}30` }}>
            <span className="text-[10px] text-muted-foreground">
              {children.length} sub-group{children.length > 1 ? 's' : ''}: {children.map(c => c.name || 'Group').join(', ')}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        {topLevelGroups.map(g => renderGroupCard(g))}
      </div>

      {ungroupedBlocks.length > 0 && (
        <div className="rounded-xl border-2 border-dashed border-muted-foreground/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-muted-foreground">Root (ungrouped)</span>
            <span className="text-xs text-muted-foreground">({ungroupedBlocks.length} blocks)</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {ungroupedBlocks.map(b => (
              <span
                key={b.id}
                className="text-[10px] px-1.5 py-0.5 rounded border"
                style={{
                  borderColor: `${b.color}40`,
                  color: b.color,
                  backgroundColor: `${b.color}10`,
                }}
              >
                {b.config.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <CrossGroupConnectors connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
    </div>
  );
}

// ─── MAIN DIALOG ────────────────────────────────────────────────────

